/**
 * NOTE on redux: this mirrors mobile's data sourcing pattern.
 *
 * The business record shown here (top section, cover image, coupon cards'
 * `business` field) comes SOLELY from `affiliateBusiness.getAffiliateBusiness(id)`
 * -> `state.affiliateBusiness.data`. `businesses/details` is deliberately NOT
 * used for display data — that endpoint is self-scoped (it returns whichever
 * business the logged-in session belongs to, ignoring the `id` in the URL).
 *
 * Coupons come from `businesses/affiliateCoupons` — the per-business affiliate
 * coupon cache — same as mobile.
 *
 * DEALS TAB — extended beyond mobile's Rakuten-only version. Mobile always
 * dispatched `rakutenActions.getCoupons({ mid })` unconditionally. Here we
 * branch on `data.affiliate_type` (the same field used when businesses are
 * created via AffiliateBusinessModal — 'rakuten' | 'cj' | 'impact' | 'awin')
 * and call the matching network's redux module. AWIN has no deals endpoint
 * in this codebase (confirmed) — coupons-only for AWIN businesses, same as
 * today.
 *
 * Field shapes differ per network — normalizeDeal() below maps each into a
 * common { id, title, description, code, url, startDate, endDate } shape:
 *   - cj:      confirmed field names (link_name, click_url, coupon_code,
 *              promotion_start_date/end_date) from the existing CJ deal
 *              mapping used elsewhere in this codebase.
 *   - impact:  confirmed name/description/promo_code/discount_* fields from
 *              the FetchImpactDeals backend parser, but the click-through
 *              URL field is a GUESS (tried url/click_url/tracking_url) —
 *              verify against a real payload.
 *   - rakuten: field names are a GUESS across common Rakuten LinkShare
 *              naming conventions (offername/offerid/clickurl etc.) — this
 *              codebase's actual Rakuten deal shape was never confirmed here.
 *              Verify against a real payload before relying on this.
 *
 * DESIGN UPDATE: this page's markup was rebuilt to match BusinessesDetails'
 * two-column layout — hero photo card with overlapping avatar circle,
 * quick-action icon rows, a Business Info card, a Get Direction card, and
 * a right column of deals. The old <AffiliateBusinessInfoTopSection>
 * component is no longer used.
 *
 * HERO IMAGE FIX: every business on this page is an affiliate business —
 * their `cover_image` frequently turns out to be a padded logo graphic
 * (from the affiliate network's merchant data) rather than a full-bleed
 * photo, unlike BusinessesDetails' user-uploaded cover photos. object-fit:
 * cover was cropping to fill the square, which faithfully displayed
 * whatever whitespace was baked into that kind of image as a visible gap.
 * Since isAffiliateOrFlyer is effectively always true here, the hero image
 * always renders with object-fit: contain (never crops, so no cropped
 * logos) plus a blurred/scaled backdrop copy of the same image behind it
 * (so there's no hard white edge around the contained image either).
 *
 * FOLLOW BUTTON — ported from CouponsDetails.js's businessPanel, which has
 * the real, working Follow/Following button this page never had. Was
 * computing `isFollowing` before but never rendering anything with it —
 * that memo is now `followRecord` (the whole matched record, not just a
 * bool) since unfollowBusinessRequest needs the record's own `id`, exactly
 * like CouponsDetails' toggleFollowHandler. isAffiliateOrFlyer is passed
 * as `true` unconditionally in the dispatch payloads (not read from
 * `data.is_affiliate_or_flyer`) since every business on this page already
 * is one, by definition of being on this route. Rendered at the bottom of
 * the Business Info card (below the about rows), not as its own
 * standalone element between heroText and the cards.
 *
 * SUB-DEALS TITLE — "Top {name} Deals" now carries today's date at the end
 * (e.g. "Top Acme Deals — July 22, 2026"), and is shown above BOTH the
 * Coupons grid/list AND the Promo Code (network deals) list, not just
 * coupons as before. formatTodayLabel() computes this from `new Date()` on
 * every render rather than being hardcoded, so it always reflects the
 * actual current date instead of going stale.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import {
  ExternalLink,
  Globe,
  Grid,
  Instagram,
  List,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShoppingBag,
  Tag,
  User,
} from 'react-feather';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { CustomerCouponCard, RequestForPoints, Spinner, SeoHead } from 'components';
import { COUPON_TYPES } from 'coupon-common';
import { actions as affiliateBusinessActions } from 'coupon-common/src/modules/affiliateBusiness';
import { actions as affiliateBusinessCouponActions } from 'coupon-common/src/modules/businesses/affiliateCoupons';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';
import { actions as cjActions } from 'coupon-common/src/modules/cj';
// Same cached slice Home/GuestHome/CouponsDetails.js already populate —
// reused here (not a dedicated request) for the "Similar coupons" rail at
// the bottom of the page.
import { actions as allCouponsActions } from 'coupon-common/src/modules/customerCoupons/all';
import { actions as favCouponActions } from 'coupon-common/src/modules/customerCoupons/favourite';
import { actions as impactActions } from 'coupon-common/src/modules/impact';
import { actions as rakutenActions } from 'coupon-common/src/modules/rakuten';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { SITE_URL } from 'components/SeoHead/SeoHead';
import NoImage from '../../../assets/images/NoCouponImage.webp';
import LoginPrompt from 'components/LoginPrompt/LoginPrompt';
import ScrollRail from 'components/ScrollRail/ScrollRail';
import { RouteNames } from '../../../constants';
import { resolveImageUrl } from '../../../utils/cleanExternalUrls';
import styles from './AffiliateBusinessDetails.module.scss';
import messages from './messages';

// Same amount/percentage/points-back switch CouponsDetails.js uses for its
// own Similar Coupons rail.
const getCouponDealAmount = coupon => {
  if (!coupon) return null;
  if (coupon.type === COUPON_TYPES.AMOUNT_OFF) return `$${coupon.amount_off} OFF`;
  if (coupon.type === COUPON_TYPES.PERCENTAGE_OFF) return `${Math.round(coupon.percentage_off)}% OFF`;
  if (coupon.type === COUPON_TYPES.POINTS_BACK) return `${coupon.points_back} Points Back`;
  return null;
};

// How many of this business's own coupons/deals show before the "More" button.
// Multiple of the grid's 3 columns, so each "more" batch fills whole rows.
const COUPONS_PAGE_SIZE = 6;


// ─── Build a human-readable discount label for Impact deals ──────────────────
const buildImpactDiscountLabel = deal => {
  const pct = parseFloat(deal.discount_percent);
  if (!isNaN(pct) && pct > 0) return `${pct}% off`;
  const amt = parseFloat(deal.discount_amount);
  if (!isNaN(amt) && amt > 0) {
    const currency = deal.discount_currency || '$';
    return `${currency} ${amt} off`;
  }
  if (deal.discount_type === 'FREE_SHIPPING' || deal.type === 'FREE_SHIPPING') return 'Free Shipping';
  return null;
};

// ─── Normalize a raw network deal into a common display shape ────────────────
const normalizeDeal = (deal, network, businessUrl) => {
  switch (network) {
    case 'CJ':
      return {
        id: deal.link_id,
        title: deal.link_name || '',
        description: deal.description || '',
        code: deal.coupon_code || '',
        url: deal.click_url || '',
        discount: null,
        startDate: deal.promotion_start_date || null,
        endDate: deal.promotion_end_date || null,
      };

    case 'IMPACT':
      return {
        id: deal.id,
        title: deal.name || '',
        description: deal.description || '',
        code: deal.promo_code || '',
        url: businessUrl || deal.url || deal.click_url || deal.tracking_url || '',
        discount: buildImpactDiscountLabel(deal),
        startDate: deal.start_date || null,
        endDate: deal.end_date || null,
      };

    case 'RAKUTEN':
    default:
      return {
        id: deal.id || deal.offerid || deal.link_id,
        title: deal.title || deal.offername || deal.name || '',
        description: deal.description || deal.offerdescription || '',
        code: deal.code || deal.couponcode || deal.coupon_code || '',
        url: deal.url || deal.clickurl || deal.click_url || deal.link || businessUrl || '',
        discount: null,
        startDate: deal.start_date || deal.startdate || null,
        endDate: deal.end_date || deal.enddate || null,
      };
  }
};

/**
 * Business coordinates are stored as [latitude, longitude] — matches the
 * same convention BusinessesDetails already assumes for its own map.
 */
const resolveCoordinate = location => {
  const coords = location?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const [latitude, longitude] = coords;
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? { lat: latitude, lng: longitude } : null;
};

/**
 * Normalizes a social handle/URL into a clickable link. Accepts either a
 * full URL or a bare handle (e.g. "mannyo" for Instagram) — if it's not
 * already a URL, builds one against the given platform's base.
 */
const toSocialUrl = (value, base) => {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const handle = value.replace(/^@/, '');
  return `${base}${handle}`;
};

const toWhatsAppUrl = phoneOrUrl => {
  if (!phoneOrUrl) return null;
  if (/^https?:\/\//i.test(phoneOrUrl)) return phoneOrUrl;
  const digitsOnly = phoneOrUrl.replace(/[^\d]/g, '');
  return digitsOnly ? `https://wa.me/${digitsOnly}` : null;
};

/**
 * Formats today's date as e.g. "July 22, 2026". Computed fresh from
 * `new Date()` (not hardcoded), so the sub-deals title always reflects
 * whatever day the page is actually rendered on.
 */
const formatTodayLabel = () =>
  new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const GOOGLE_MAPS_LIBRARIES = [];

/**
 * Deals-tab card (CJ/Rakuten/Impact network deals) — restyled to match
 * CustomerCouponCard's list layout (see CustomerCouponCard.module.scss's
 * "List layout" section), with two deliberate departures from it:
 *
 *  1. No photo — network deals don't have one worth showing (mobile never
 *     rendered an image for these either). The image slot is replaced with
 *     `deal.discount` rendered as large text in the same brand gradient
 *     .list-deal-amount uses, so the slot still carries real information
 *     instead of sitting empty.
 *  2. The old footer (small code chip + separate "Shop Now" link) is
 *     replaced with a code-reveal control: a stroked pill holding the
 *     plain `code` text, with a solid red-gradient "Show Code" button
 *     overlapping it from the left (per the reference image). Clicking it
 *     only reveals the code — no navigation — since these are one-time
 *     "click to peek" affordances by design, not primary CTAs.
 *
 * Needs its own `useState` for the reveal toggle, which is why this is a
 * separate component rather than inline JS inside the `.map()` in the
 * parent (can't call hooks conditionally/per-iteration inside a loop).
 * Falls back to the old "Shop Now" link (or "Link unavailable") when a
 * deal has no `code` at all — the reveal control only makes sense when
 * there's actually a code underneath it.
 */
const NetworkDealCard = ({ deal }) => {
  const [showCode, setShowCode] = useState(false);

  return (
    <div className={styles.dealListCard}>
      <div className={styles.dealValuePanel}>
        {deal.discount ? (
          <span className={styles.dealValueText}>{deal.discount}</span>
        ) : (
          <Tag size={28} className={styles.dealValueIcon} />
        )}
      </div>

      <div className={styles.dealBody}>
        <div className={styles.dealTextCol}>
          <h4 className={styles.dealTitle}>{deal.title}</h4>
          {deal.description && <p className={styles.dealDescription}>{deal.description}</p>}
        </div>

        {/* Right-hand column, stretched to the row's full height —
            Visit site (if a merchant link exists) pins to the TOP-right
            corner; the code control / Shop Now pins to the BOTTOM-right
            corner via .dealActionBottom's margin-top: auto. These are two
            separate corners now, not one group aligned together. */}
        <div className={styles.dealActionCol}>
          {deal.url && (
            <a href={deal.url} target="_blank" rel="noopener noreferrer" className={styles.dealShopLinkSecondary}>
              Visit site →
            </a>
          )}

          <div className={styles.dealActionBottom}>
            {deal.code ? (
              <div className={styles.dealCodeWrap}>
                <div className={`${styles.dealCodeChip} ${showCode ? styles.dealCodeChipRevealed : ''}`}>
                  <span className={styles.dealCodeText}>{deal.code}</span>
                </div>
                {!showCode && (
                  <button
                    type="button"
                    className={styles.dealCodeRevealBtn}
                    onClick={() => setShowCode(true)}
                  >
                    Show Code
                  </button>
                )}
              </div>
            ) : deal.url ? (
              <a href={deal.url} target="_blank" rel="noopener noreferrer" className={styles.dealShopLink}>
                Shop Now →
              </a>
            ) : (
              <span
                className={styles.dealShopLinkDisabled}
                title="No link found for this deal — check the network's raw payload field name"
              >
                Link unavailable
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AffiliateBusinessDetails = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = idParam ? Number(idParam) : null;

  // 'coupons' | 'deals' — which content type the right column shows.
  const [selectedTab, setSelectedTab] = useState('coupons');
  // 'grid' | 'list' — only meaningful while selectedTab === 'coupons'.
  const [viewMode, setViewMode] = useState('grid');

  const [loginPromptVisible, setLoginPromptVisible] = useState(false);
  const [visibleCouponsCount, setVisibleCouponsCount] = useState(COUPONS_PAGE_SIZE);
  const [visibleDealsCount, setVisibleDealsCount] = useState(COUPONS_PAGE_SIZE);

  // Reset both back to the first page whenever the business itself changes.
  useEffect(() => {
    setVisibleCouponsCount(COUPONS_PAGE_SIZE);
    setVisibleDealsCount(COUPONS_PAGE_SIZE);
  }, [id]);

  // List view is desktop-only, mirroring BusinessesDetails' own
  // breakpoint (900px, where the two-column layout stacks to one).
  const [isSmallScreen, setIsSmallScreen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 900,
  );

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 900px)');
    const handleChange = e => setIsSmallScreen(e.matches);
    handleChange(mql);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  const effectiveViewMode = isSmallScreen ? 'grid' : viewMode;

  // Sole source of the business record — fetched by id, matching mobile.
  const affiliateState = useSelector(state => state.affiliateBusiness);
  const { fetching, fetched, data, error } = affiliateState || {};

  const followState = useSelector(state => state.businesses.follow);
  const authorized = useSelector(state => !!(state.auth && state.auth.authorized));
  const allFavourite = useSelector(state => state.customerCoupons.favourite);
  const favouriteList = allFavourite?.data;

  // Per-business affiliate coupon cache — same module/shape mobile uses.
  const businessCouponsState = useSelector(state => (id && state.businesses.affiliateCoupons?.[id]) || {});
  const couponsData = businessCouponsState?.data;

  // Per-network deal caches.
  const rakutenState = useSelector(state => state.rakuten || {});
  const cjState = useSelector(state => state.cj || {});
  const impactState = useSelector(state => state.impact || {});
  const rakutenDeals = rakutenState.coupons || [];
  const cjDeals = cjState.deals || [];
  const impactDeals = impactState.deals || [];

  const affiliateType = data?.affiliate_type || null;


  const rawDeals = useMemo(() => {
    switch (affiliateType) {
      case 'RAKUTEN':
        return rakutenDeals;
      case 'CJ':
        return cjDeals;
      case 'IMPACT':
        return impactDeals;
      default:
        return [];
    }
  }, [affiliateType, rakutenDeals, cjDeals, impactDeals]);

  const deals = useMemo(
    () => (rawDeals || []).map(deal => normalizeDeal(deal, affiliateType, data?.business_web_url)),
    [rawDeals, affiliateType, data?.business_web_url],
  );

  // ─── Follow state — same pattern as CouponsDetails.js's businessPanel ───
  // followRecord (not just a bool) since unfollowBusinessRequest needs the
  // matched record's own `id`, not just the business id.
  const followRecord = useMemo(
    () => (followState?.data?.results || []).find(b => b.business_id === id),
    [followState?.data?.results, id],
  );
  const isBusinessFollowed = !!followRecord;

  const toggleFollowHandler = useCallback(() => {
    if (!authorized) {
      setLoginPromptVisible(true);
      return;
    }
    if (!id) return;
    if (isBusinessFollowed) {
      dispatch(
        followActions.unfollowBusinessRequest({
          id: followRecord?.id,
          business_id: id,
          // Every business on this page is affiliate by definition of the
          // route it's on — unlike CouponsDetails, which reads this off
          // the coupon's own `is_affiliate_or_flyer` field since it can
          // show either business type.
          is_affiliate_or_flyer: true,
        }),
      );
    } else {
      dispatch(
        followActions.followBusinessRequest({
          business_id: id,
          is_affiliate_or_flyer: true,
        }),
      );
    }
  }, [dispatch, id, isBusinessFollowed, followRecord, authorized]);

  const couponsRaw = useMemo(() => {
    if (
      !couponsData ||
      (Array.isArray(couponsData) && couponsData.length === 0) ||
      (!Array.isArray(couponsData) && Object.keys(couponsData).length === 0)
    ) {
      return [];
    }
    return Array.isArray(couponsData) ? couponsData : [couponsData];
  }, [couponsData]);

  const filteredCoupons = useMemo(() => {
    if (!couponsRaw.length) return [];
    const now = new Date();
    return couponsRaw.filter(item => (item.expiry_type === 'amount_limit' ? true : new Date(item.expiry) > now));
  }, [couponsRaw]);

  const visibleCoupons = useMemo(
    () => filteredCoupons.slice(0, visibleCouponsCount),
    [filteredCoupons, visibleCouponsCount],
  );
  const visibleDeals = useMemo(() => deals.slice(0, visibleDealsCount), [deals, visibleDealsCount]);

  const noContent = filteredCoupons.length === 0 && deals.length === 0;

  // ─── Similar coupons (other businesses) ───────────────────────────────
  // Same cached customerCoupons/all slice Home/GuestHome/CouponsDetails.js
  // already populate — reused as-is, fetched once here as a fallback if a
  // visitor lands directly on this page with nothing cached yet.
  const customerCouponsAllState = useSelector(state => state.customerCoupons.all || {});
  const { items: allCouponItems, result: allCouponResult, fetching: allCouponsFetching } = customerCouponsAllState;
  const activeCityId = useSelector(state => (state.cities && state.cities.activeCityId) || null);

  // "Similar coupons" means the same category(ies) this business's own
  // coupons belong to — same id/name matching CustomerCouponListAll/
  // Favourites already use for category filtering. Falls back to the
  // unfiltered (business-excluded) list when this business's coupons have
  // no category to match against.
  const businessCategoryKeys = useMemo(() => {
    const keys = filteredCoupons
      .map(item => item.category?.id ?? (item.category?.name || (typeof item.category === 'string' ? item.category : null)))
      .filter(Boolean);
    return new Set(keys);
  }, [filteredCoupons]);

  const similarCoupons = useMemo(() => {
    const ids = allCouponResult?.results || [];
    const otherBusinessCoupons = ids
      .map(rid => allCouponItems?.[rid])
      .filter(item => item && item.business?.id !== id);

    if (businessCategoryKeys.size === 0) return otherBusinessCoupons.slice(0, 10);

    return otherBusinessCoupons
      .filter(item => {
        const itemCategoryKey = item.category?.id ?? (item.category?.name || (typeof item.category === 'string' ? item.category : null));
        return itemCategoryKey && businessCategoryKeys.has(itemCategoryKey);
      })
      .slice(0, 10);
  }, [allCouponResult, allCouponItems, id, businessCategoryKeys]);

  const hasFetchedSimilar = useRef(false);
  useEffect(() => {
    if (hasFetchedSimilar.current) return;
    if (similarCoupons.length > 0 || allCouponsFetching) return;
    hasFetchedSimilar.current = true;
    dispatch(allCouponsActions.getAllList({ page: 1, ...(activeCityId ? { city: activeCityId } : {}) }));
  }, [similarCoupons.length, allCouponsFetching, activeCityId, dispatch]);

  const checkFavourite = useCallback(
    coupon => favouriteList?.results?.some(favCoupon => favCoupon.id === coupon.id) ?? false,
    [favouriteList],
  );

  const goToDetails = couponId => navigate(`${RouteNames.COUPONS_DETAILS}/${couponId}`);
  const goToThisBusiness = () => navigate(`${RouteNames.AFFILIATE_BUSINESS_DETAILS}/${id}`);

  const toggleFavouriteHandler = (couponId, isFavourite) => {
    const action = isFavourite ? favCouponActions.removeFavourite(couponId) : favCouponActions.markAsFavourite(couponId);
    dispatchAsAsync(action, dispatch).then(() => {
      dispatch(favCouponActions.getAllFavouriteList());
    });
  };

  // Business record + follow state + coupons.
  useEffect(() => {
    if (!id) return;
    dispatch(followActions.getFollowedBusinessesRequest());
    dispatch(affiliateBusinessActions.getAffiliateBusiness(id));
    dispatch(affiliateBusinessCouponActions.getDetail(id));
  }, [dispatch, id]);

  // Deals — only once we know which network this business belongs to, and
  // only fires the ONE matching network's fetch (not all three).
  useEffect(() => {
    if (!id || !data?.affiliate_code || !affiliateType) return;
    switch (affiliateType) {
      case 'RAKUTEN':
        dispatch(rakutenActions.getCoupons({ mid: data.affiliate_code }));
        break;
      case 'CJ':
        dispatch(cjActions.getCJDeals({ advertiser_id: data.affiliate_code, discount_only: 'true' }));
        break;
      case 'IMPACT':
        dispatch(impactActions.getImpactDeals({ campaign_id: data.affiliate_code }));
        break;
      default:
        break; // 'awin' has no deals endpoint in this codebase — coupons-only, confirmed
    }
  }, [dispatch, id, data?.affiliate_code, affiliateType]);

  useEffect(() => {
    if (!id) return;
    if (filteredCoupons.length > 0) {
      setSelectedTab('coupons');
    } else if (deals.length > 0) {
      setSelectedTab('deals');
    } else {
      setSelectedTab(null);
    }
  }, [id, filteredCoupons.length, deals.length]);

  // ─── Utility Action Handlers ─────────────────────────────────────────
  const openPhoneNumber = phone => {
    if (phone) window.location.href = `tel:${phone}`;
  };

  const handleVisitWebsite = url => {
    if (url) {
      const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      window.open(withScheme, '_blank', 'noopener,noreferrer');
    }
  };

  const openExternalLink = url => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const coordinate = resolveCoordinate(data?.location);

  const openMapOnLocation = () => {
    if (!coordinate) return;
    navigate(RouteNames.COUPONS_MAPS_ALL, {
      state: { dest: { latitude: coordinate.lat, longitude: coordinate.lng } },
    });
  };

  const handleOpenExternalDirections = () => {
    if (!coordinate) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${coordinate.lat},${coordinate.lng}&travelmode=driving`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const { isLoaded: isMapLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  if (!id) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <p className={styles.stateText}>Business not found.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <p className={styles.stateText}>Something went wrong loading this business.</p>
        </div>
      </div>
    );
  }

  if (fetching && !fetched) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap} style={{ display: 'flex', justifyContent: 'center', paddingTop: '6rem' }}>
          <Spinner show size="lg" />
        </div>
      </div>
    );
  }

  const {
    name,
    logo,
    cover_image: coverImage,
    business_web_url: websiteUrl,
    disclosure,
    is_affiliate_or_flyer: isAffiliateOrFlyer,
    // Best-guess field names, same caveat as BusinessesDetails — confirm
    // against the actual affiliateBusiness API response.
    phone,
    street,
    city,
    postal_code: postalCode,
    owner_name: ownerName,
    instagram_url: instagramUrl,
    telegram_url: telegramUrl,
    whatsapp_number: whatsappNumber,
  } = data || {};


  const address1 = street || '';
  const address2 = `${city?.name || ''}${postalCode ? `, ${postalCode}` : ''}`;

  const instagramHref = toSocialUrl(instagramUrl, 'https://instagram.com/');
  const telegramHref = toSocialUrl(telegramUrl, 'https://t.me/');
  const whatsappHref = toWhatsAppUrl(whatsappNumber);

  // "Top {name} Deals — {today's date}" — shared by both the Coupons and
  // Promo Code (network deals) sections below.
  const subDealsTitle = name ? `Top ${name} Deals — ${formatTodayLabel()}` : null;


    
  // ─── SEO ──────────────────────────────────────────────────────────
const businessPath = `${RouteNames.AFFILIATE_BUSINESS_DETAILS}/${id}`;
const seoTitle = name ? `Top ${name} Promo Codes & Coupons | My Couponbook` : undefined;
const seoDescription = name
  ? `Verified ${name} promo codes and deals for Canadian shoppers. Save money on your next purchase and earn rewards.`
  : undefined;
// const seoDescription = data
//   ? `${name ? `${name} — ` : ''}${address2 ? `Located in ${address2}. ` : ''}Find deals, coupons, and promo codes from ${
//       name || 'this business'
//     } on My Couponbook.`
//   : undefined;
const structuredData = data
  ? {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name,
      image: coverImage || logo || undefined,
      logo: logo || undefined,
      telephone: phone || undefined,
      url: `${SITE_URL}${businessPath}`,
      address:
        address1 || address2
          ? {
              '@type': 'PostalAddress',
              streetAddress: address1 || undefined,
              addressLocality: city?.name || undefined,
              postalCode: postalCode || undefined,
            }
          : undefined,
    }
  : undefined;

  return (
    <div className={styles.page}>
      <SeoHead
       title={seoTitle}
       description={seoDescription}
       image={coverImage || logo}
       path={businessPath}
       type="profile"
       structuredData={structuredData}
     />
      <div className={styles.wrap}>
        <div className={styles.layout}>
          {/* ─── Left column: profile, info, directions ─── */}
          <div className={styles.leftCol}>
            <div className={styles.heroCard}>
              <div className={styles.heroImageWrap}>
                <div className={styles.heroImageClip}>
                  <img
                    src={coverImage || 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1200&q=80'}
                    alt={`${name || 'Business'} cover`}
                    className={styles.heroImage}
                  />
                  <div className={styles.heroOverlay} />

                  <div className={styles.heroIconsRow}>
                    {websiteUrl && (
                      <button type="button" className={styles.iconBtn} onClick={() => handleVisitWebsite(websiteUrl)} title="Website">
                        <Globe size={17} />
                      </button>
                    )}
                    {phone && (
                      <button type="button" className={styles.iconBtn} onClick={() => openPhoneNumber(phone)} title="Call Store">
                        <Phone size={16} />
                      </button>
                    )}
                    {coordinate && (
                      <button type="button" className={styles.iconBtn} onClick={openMapOnLocation} title="Directions">
                        <MapPin size={17} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.logoRing}>
                {logo ? (
                  <img src={logo} alt={name || ''} className={styles.logoCircle} />
                ) : (
                  <div className={styles.logoFallback}>{name?.charAt(0) || 'B'}</div>
                )}
              </div>

              {(instagramHref || telegramHref || whatsappHref) && (
                <div className={styles.socialIconsRow}>
                  {instagramHref && (
                    <button type="button" className={styles.iconBtn} onClick={() => openExternalLink(instagramHref)} title="Instagram">
                      <Instagram size={16} />
                    </button>
                  )}
                  {telegramHref && (
                    <button type="button" className={styles.iconBtn} onClick={() => openExternalLink(telegramHref)} title="Telegram">
                      <Send size={16} />
                    </button>
                  )}
                  {whatsappHref && (
                    <button type="button" className={styles.iconBtn} onClick={() => openExternalLink(whatsappHref)} title="WhatsApp">
                      <MessageCircle size={16} />
                    </button>
                  )}
                </div>
              )}

              {isAffiliateOrFlyer && <span className={styles.affiliateBadge}>Affiliate Partner</span>}
              {isAffiliateOrFlyer && disclosure && <p className={styles.disclosure}>{disclosure}</p>}
            </div>

            <div className={styles.heroText}>
              <h1 className={styles.heroName}>{name}</h1>
            </div>

            {data?.has_joined_points_program && (
              <div className={styles.card}>
                <RequestForPoints businessId={id} />
              </div>
            )}

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <ShoppingBag size={18} className={styles.cardHeaderIcon} />
                <h2 className={styles.cardHeaderTitle}>Business Info</h2>
              </div>

              <div className={styles.aboutList}>
                {name && (
                  <div className={styles.aboutRow}>
                    <span className={styles.aboutIcon}>
                      <ShoppingBag size={16} />
                    </span>
                    <span className={styles.aboutValue}>{name}</span>
                  </div>
                )}
                {ownerName && (
                  <div className={styles.aboutRow}>
                    <span className={styles.aboutIcon}>
                      <User size={16} />
                    </span>
                    <span className={styles.aboutValue}>{ownerName}</span>
                  </div>
                )}
                {phone && (
                  <div className={styles.aboutRow}>
                    <span className={styles.aboutIcon}>
                      <Phone size={16} />
                    </span>
                    <span className={styles.aboutValue}>{phone}</span>
                  </div>
                )}
                {(address1 || address2) && (
                  <div className={styles.aboutRow}>
                    <span className={styles.aboutIcon}>
                      <MapPin size={16} />
                    </span>
                    <span className={styles.aboutValue}>
                      {address1}
                      {address1 && address2 ? ', ' : ''}
                      {address2}
                    </span>
                  </div>
                )}
              </div>

              {/* Follow / Following — moved inside Business Info, at the
                  bottom of the card (was a standalone button between
                  heroText and this card). Its own wrapper carries the
                  same horizontal/bottom padding .aboutList uses, so it
                  lines up with the rows above instead of touching the
                  card's edges. */}
              <div className={styles.followBtnWrap}>
                <button
                  type="button"
                  className={isBusinessFollowed ? styles.followBtn : `${styles.followBtn} ${styles.notFollowing}`}
                  onClick={toggleFollowHandler}
                >
                  {isBusinessFollowed ? 'Following' : 'Follow'}
                </button>
              </div>
            </div>

            {coordinate && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <MapPin size={18} className={styles.cardHeaderIcon} />
                  <h2 className={styles.cardHeaderTitle}>Get Direction</h2>
                  <button
                    type="button"
                    className={styles.cardHeaderAction}
                    onClick={handleOpenExternalDirections}
                    title="Open in Google Maps"
                  >
                    <ExternalLink size={19} />
                  </button>
                </div>

                <div className={styles.mapFrame}>
                  {isMapLoaded && GOOGLE_MAPS_API_KEY ? (
                    <GoogleMap
                      center={coordinate}
                      zoom={14}
                      options={{ disableDefaultUI: true, gestureHandling: 'cooperative' }}
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      onClick={openMapOnLocation}
                    >
                      <Marker position={coordinate} onClick={openMapOnLocation} />
                    </GoogleMap>
                  ) : (
                    <button type="button" className={styles.mapFallback} onClick={openMapOnLocation}>
                      <MapPin size={18} />
                      &nbsp;Tap to view on the map
                    </button>
                  )}
                </div>
              </div>
            )}
            <text className={styles.disclaimer}> My Couponbook may earn a commission when you buy through links on our site.</text>
          </div>
          

          {/* ─── Right column: Coupons / Deals ─── */}
          <div className={styles.rightCol}>
            <div className={styles.dealsHeader}>
              {filteredCoupons.length > 0 && deals.length > 0 ? (
                <div className={styles.contentTabRow}>
                  <button
                    type="button"
                    className={`${styles.contentTabBtn} ${selectedTab === 'coupons' ? styles.active : ''}`}
                    onClick={() => setSelectedTab('coupons')}
                  >
                    Coupons
                  </button>
                  <button
                    type="button"
                    className={`${styles.contentTabBtn} ${selectedTab === 'deals' ? styles.active : ''}`}
                    onClick={() => setSelectedTab('deals')}
                  >
                    Promo Code
                  </button>
                </div>
              ) : (
                <h2 className={styles.dealsTitle}>{selectedTab === 'deals' ? 'Deals' : 'Coupons'}</h2>
              )}

              {selectedTab === 'coupons' && filteredCoupons.length > 0 && (
                <div className={styles.viewToggle}>
                  <button
                    type="button"
                    className={`${styles.viewToggleBtn} ${viewMode === 'grid' ? styles.active : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid size={14} /> Grid
                  </button>
                  <button
                    type="button"
                    className={`${styles.viewToggleBtn} ${styles.listToggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <List size={14} /> List
                  </button>
                </div>
              )}
            </div>

            {selectedTab === 'coupons' && filteredCoupons.length > 0 && (
              <>
                {subDealsTitle && <h3 className={styles.subDealsTitle}>{subDealsTitle}</h3>}
                <div className={effectiveViewMode === 'grid' ? styles.dealsGrid : styles.dealsListWrap}>
                  {visibleCoupons.map((coupon, index) => (
                    <CustomerCouponCard
                      key={coupon.id}
                      first={index === 0}
                      data={{ ...coupon, business: data }}
                      layout={effectiveViewMode}
                      // Same look as BusinessesDetails: ticket-style list card
                      // and no dark gradient over the photos.
                      variant="business"
                      onPress={() => goToDetails(coupon.id)}
                      goToBusinessDetails={goToThisBusiness}
                      isFavourite={checkFavourite(coupon)}
                      toggleFavourite={() => toggleFavouriteHandler(coupon.id, checkFavourite(coupon))}
                    />
                  ))}
                </div>
                {filteredCoupons.length > visibleCoupons.length && (
                  <button
                    type="button"
                    className={styles.moreCouponsBtn}
                    onClick={() => setVisibleCouponsCount(c => c + COUPONS_PAGE_SIZE)}
                  >
                    More
                  </button>
                )}
              </>
            )}

            {selectedTab === 'deals' && deals.length > 0 && (
              <>
                {subDealsTitle && <h3 className={styles.subDealsTitle}>{subDealsTitle}</h3>}
                <div className={styles.networkDealsList}>
                  {visibleDeals.map((deal, index) => (
                    <NetworkDealCard deal={deal} key={deal.id ?? index} />
                  ))}
                </div>
                {deals.length > visibleDeals.length && (
                  <button
                    type="button"
                    className={styles.moreCouponsBtn}
                    onClick={() => setVisibleDealsCount(c => c + COUPONS_PAGE_SIZE)}
                  >
                    More
                  </button>
                )}
              </>
            )}

            {noContent && <p className={styles.stateText}>{intl.formatMessage(messages.noCouponsMessage)}</p>}
          </div>
        </div>

        {/* ─── Similar coupons (other businesses) ─── */}
        {similarCoupons.length > 0 && (
          <div className={styles.similarSection}>
            <h2 className={styles.similarTitle}>Similar coupons</h2>
            <ScrollRail className={styles.similarScroll} ariaLabel="similar coupons">
              {similarCoupons.map(item => {
                const itemImage = resolveImageUrl(item.image_url?.[0]?.image_url) || NoImage;
                const itemCategoryName = item.category?.name || (typeof item.category === 'string' ? item.category : null);
                const itemDealAmount = getCouponDealAmount(item);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={styles.similarCard}
                    onClick={() => navigate(`${RouteNames.COUPONS_DETAILS}/${item.id}`)}
                  >
                    <img src={itemImage} alt="" className={styles.similarCardImg} />
                    <div className={styles.similarCardOverlay} />
                    <div className={styles.similarBadgeRow}>
                      {itemCategoryName && <span className={styles.badgePillWhite}>{itemCategoryName}</span>}
                      {item.is_exclusive_paid && <span className={styles.badgePillGold}>Premium</span>}
                    </div>
                    <div className={styles.similarCardText}>
                      <p className={styles.similarCardTitle}>{itemDealAmount || item.title}</p>
                      {itemDealAmount && <p className={styles.similarCardSubtitle}>{item.title}</p>}
                    </div>
                  </button>
                );
              })}
            </ScrollRail>
          </div>
        )}
      </div>
      <LoginPrompt
        isOpen={loginPromptVisible}
        onClose={() => setLoginPromptVisible(false)}
      />
    </div>
  );
};

export default AffiliateBusinessDetails;